import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { CloudUpload } from 'lucide-react';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { useAuthStore } from '../../store/authStore';
import { getErrorMessage } from '../../lib/apiClient';
import { formatPrice, planPrice, planPriceSub } from '../pricing/lib/pricing';
import { getPricing, pricingKeys } from '../pricing/pricingApi';
import { registerRequest } from './authApi';
import AuthShell from './AuthShell';
import GoogleButton from './GoogleButton';

const PASSWORD_RULE =
  /(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^\w\s])/;

function PlanPicker({ plans, cycle, onCycle, selectedId, onSelect, onContinue }) {
  return (
    <div className="space-y-4">
      <div className="flex justify-center gap-1 rounded-full border border-slate-200 bg-slate-50 p-1">
        {['monthly', 'annual'].map((value) => (
          <button
            key={value}
            type="button"
            onClick={() => onCycle(value)}
            className={clsx(
              'flex-1 rounded-full px-3 py-1.5 text-sm font-semibold capitalize transition',
              cycle === value ? 'bg-brand-600 text-white' : 'text-slate-500 hover:text-slate-800',
            )}
          >
            {value}
            {value === 'annual' && <span className="ml-1 text-[11px]">— save 25%</span>}
          </button>
        ))}
      </div>

      <div className="space-y-2" role="radiogroup" aria-label="Choose a plan">
        {plans.map((plan) => {
          const { amount, unit } = planPrice(plan, cycle);
          const selected = plan.id === selectedId;
          return (
            <button
              key={plan.id}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => onSelect(plan.id)}
              className={clsx(
                'w-full rounded-xl border p-4 text-left transition',
                selected
                  ? 'border-brand-600 bg-brand-50'
                  : 'border-slate-200 bg-slate-50 hover:border-slate-300',
              )}
            >
              <div className="flex items-baseline justify-between">
                <span className="font-semibold text-slate-900">
                  {plan.name}
                  {plan.badge ? (
                    <span className="ml-2 rounded-full bg-brand-600 px-2 py-0.5 text-[10px] font-bold uppercase text-white">
                      {plan.badge}
                    </span>
                  ) : null}
                </span>
                <span className="font-bold text-slate-900">
                  {amount}
                  {unit && <span className="text-xs font-normal text-slate-500">{unit}</span>}
                </span>
              </div>
              <p className="mt-1 text-xs text-slate-500">{planPriceSub(plan, cycle)}</p>
              {plan.trialDays > 0 && (
                <p className="mt-1 text-xs font-medium text-green-600">
                  {plan.trialDays}-day free trial
                </p>
              )}
            </button>
          );
        })}
      </div>

      <Button className="w-full" disabled={!selectedId} onClick={onContinue}>
        Continue
      </Button>
      <p className="text-center text-xs text-slate-500">
        Need enterprise features?{' '}
        <a href="mailto:sales@mydocuploads.com" className="font-medium text-brand-600">
          Talk to sales
        </a>
      </p>
    </div>
  );
}

export default function RegisterPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const setSession = useAuthStore((s) => s.setSession);
  // Deep link (e.g. an emailed /join/:token invite) carried through /login.
  const from = location.state?.from?.pathname;

  // Plans are self-serve only (active + public + priced). If the pricing API is
  // unreachable, fall back to plain registration so signup is never blocked.
  const pricingQuery = useQuery({
    queryKey: pricingKeys.all,
    queryFn: getPricing,
    staleTime: 5 * 60_000,
  });
  const plans = (pricingQuery.data?.plans ?? []).filter((plan) => !plan.isCustomPricing);
  const plansAvailable = plans.length > 0;

  const cycleParam = searchParams.get('cycle');
  const [cycle, setCycle] = useState(cycleParam === 'annual' ? 'annual' : 'monthly');
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [step, setStep] = useState('plan');

  // Preselect from ?plan=<slug> (pricing page CTA) and jump to the account step.
  useEffect(() => {
    const slug = searchParams.get('plan');
    if (!slug || selectedPlanId || plans.length === 0) return;
    const match = plans.find((plan) => plan.slug === slug);
    if (match) {
      setSelectedPlanId(match.id);
      setStep('account');
    }
  }, [plans, searchParams, selectedPlanId]);

  const selectedPlan = plans.find((plan) => plan.id === selectedPlanId) ?? null;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: { displayName: '', email: '', password: '', organizationName: '' },
  });

  const mutation = useMutation({
    mutationFn: registerRequest,
    onSuccess: (data, variables) => {
      setSession(data);
      toast.success('Account created');
      // Plan chosen → card-before-entry billing step; otherwise return to the
      // deep link that brought the user here (e.g. /join/:token) or /files.
      navigate(variables.planId ? '/billing/setup' : from || '/files', { replace: true });
    },
    onError: (error) => toast.error(getErrorMessage(error, 'Registration failed')),
  });

  const submit = (values) => {
    mutation.mutate({
      ...values,
      organizationName: values.organizationName?.trim() || undefined,
      ...(selectedPlan ? { planId: selectedPlan.id, billingCycle: cycle } : {}),
    });
  };

  const showPlanStep = plansAvailable && step === 'plan';

  return (
    <AuthShell
      icon={<CloudUpload className="h-7 w-7 text-brand-600" />}
      title={showPlanStep ? 'Choose your plan' : 'Create your account'}
      subtitle={
        showPlanStep
          ? 'Every plan starts with a free trial — billing is per organization.'
          : 'Start uploading in seconds.'
      }
    >
      {pricingQuery.isLoading ? (
        <div className="py-8 text-center text-sm text-slate-500">Loading plans…</div>
      ) : showPlanStep ? (
        <PlanPicker
          plans={plans}
          cycle={cycle}
          onCycle={setCycle}
          selectedId={selectedPlanId}
          onSelect={setSelectedPlanId}
          onContinue={() => selectedPlanId && setStep('account')}
        />
      ) : (
        <>
          {selectedPlan && (
            <div className="mb-4 flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm">
              <span>
                <span className="font-semibold text-slate-800">{selectedPlan.name}</span>{' '}
                <span className="text-slate-500">
                  {cycle === 'annual'
                    ? `${formatPrice(selectedPlan.priceAnnualCents)}/mo billed annually`
                    : `${formatPrice(selectedPlan.priceMonthlyCents)}/mo`}
                  {selectedPlan.trialDays > 0 ? ` · ${selectedPlan.trialDays}-day trial` : ''}
                </span>
              </span>
              <button
                type="button"
                onClick={() => setStep('plan')}
                className="font-medium text-brand-600 hover:text-brand-700"
              >
                Change
              </button>
            </div>
          )}
          <form className="space-y-4" onSubmit={handleSubmit(submit)} noValidate>
            <Input
              label="Full name"
              autoComplete="name"
              error={errors.displayName?.message}
              {...register('displayName', {
                required: 'Name is required',
                maxLength: { value: 120, message: 'Name is too long' },
              })}
            />
            <Input
              label="Email"
              type="email"
              autoComplete="email"
              error={errors.email?.message}
              {...register('email', {
                required: 'Email is required',
                pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email' },
              })}
            />
            <Input
              label="Password"
              type="password"
              autoComplete="new-password"
              hint="8+ chars with upper, lower, number, and symbol."
              error={errors.password?.message}
              {...register('password', {
                required: 'Password is required',
                minLength: { value: 8, message: 'At least 8 characters' },
                validate: (v) =>
                  PASSWORD_RULE.test(v) || 'Include upper, lower, number, and a symbol',
              })}
            />
            <Input
              label="Organization name (optional)"
              autoComplete="organization"
              hint="Your files and team live under it — defaults to your name"
              error={errors.organizationName?.message}
              {...register('organizationName', {
                maxLength: { value: 160, message: 'Organization name is too long' },
              })}
            />
            <Button type="submit" className="w-full" loading={mutation.isPending}>
              {selectedPlan ? 'Create account & continue' : 'Create account'}
            </Button>
          </form>
          <GoogleButton planId={selectedPlan?.id} billingCycle={cycle} />
        </>
      )}
      <p className="mt-6 text-center text-sm text-slate-500">
        Already have an account?{' '}
        <Link to="/login" className="font-medium text-brand-600 hover:text-brand-700">
          Sign in
        </Link>
      </p>
    </AuthShell>
  );
}
